import { PageHeader } from '@/components/layout/PageHeader';
import { PageShell } from '@/components/layout/PageShell';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import type { AgentSummary } from '@innermate/shared';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { Bot, Plus } from 'lucide-react';

export function AdminAgentsList() {
  const navigate = useNavigate();

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'agents'],
    queryFn: () => api.adminAgentList(),
  });

  return (
    <PageShell>
      <PageHeader
        title="에이전트"
        description="외부 서비스(예: wholesale)에 노출하는 채팅 에이전트. skill 1 → agent N 매핑."
        actions={
          <Button onClick={() => navigate({ to: '/admin/agents/new' })} size="sm">
            <Plus className="mr-1 h-4 w-4" />새 에이전트
          </Button>
        }
      />

      {isLoading && <div className="p-6 text-sm text-muted-foreground">불러오는 중...</div>}
      {error && (
        <div className="p-6 text-sm text-destructive">
          {error instanceof Error ? error.message : '불러오기 실패'}
        </div>
      )}
      {data && data.agents.length === 0 && (
        <div className="p-6 text-sm text-muted-foreground">
          등록된 에이전트가 없습니다. 우측 상단의 "새 에이전트" 버튼으로 시작하세요.
        </div>
      )}
      {data && data.agents.length > 0 && (
        <div className="grid gap-3 p-4 md:grid-cols-2 lg:grid-cols-3">
          {data.agents.map((a) => (
            <AgentCard key={a.id} agent={a} />
          ))}
        </div>
      )}
    </PageShell>
  );
}

function AgentCard({ agent }: { agent: AgentSummary }) {
  return (
    <Link
      to="/admin/agents/$id"
      params={{ id: agent.id }}
      className="block rounded-lg border bg-card p-4 transition hover:border-primary"
    >
      <div className="mb-2 flex items-center gap-2">
        <Bot className="h-4 w-4 text-muted-foreground" />
        <div className="font-semibold">{agent.name}</div>
        {!agent.isEnabled && (
          <span className="ml-auto rounded-full bg-muted px-2 py-0.5 text-[10px] text-muted-foreground">
            비활성
          </span>
        )}
      </div>
      <div className="font-mono text-xs text-muted-foreground">{agent.slug}</div>
      {agent.description && (
        <div className="mt-2 line-clamp-2 text-xs text-muted-foreground">{agent.description}</div>
      )}
      <div className="mt-3 flex items-center justify-between text-[11px] text-muted-foreground">
        <span>
          skill:{' '}
          {agent.skillNames && agent.skillNames.length > 0
            ? agent.skillNames.join(', ')
            : agent.skillIds.map((id) => id.slice(0, 8)).join(', ')}
        </span>
        <span>{agent.rateLimitPerMin}/min</span>
      </div>
    </Link>
  );
}
